import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppDemo } from "./AppDemo";
import { AppTimeZone } from "./AppTimeZone";
import { AppAssistCitations } from "./AppAssistCitations";

// AppAssistApi - "ask AI about this chat": the body the page sends to `/assist` and the reading of
// what comes back.
//
// ⚠ RULES ONLY - NO `XMLHttpRequest`, NO `window`. The Bridge suite imports this file to pin the
// field names and limits against `AssistRules` (`AssistContract.test.ts`), and a transport in here
// would pass under tsx and fail under tsc - the trap `AppConnect`/`AppConnectWatch` records.
//
// ⛔ DECISION 9: THE AI NEVER SENDS. `Draft` is text for the composer and nothing more - the page
// puts it in the box and the owner presses Send, which goes through `/send` like any typed message.
// Nothing built on this file may queue a draft on its own.

/** One earlier question and answer of this panel session. Must match `AssistRules.TurnsIn`. */
/** A service answer's body (`AppAssistApi.AnswerDocOf`): the JSON object, or null; and whether `ok` is a boolean. */
export class AppAnswerDoc {
    public Doc: Record<string, unknown> | null = null;
    public HasOk: boolean = false;
}

export class AppAssistTurn {
    public Ask: string = "";
    public Answer: string = "";
}

/**
 * A task `/tasks` has just confirmed done - the "Let <name> know?" ask (`AppTaskNotifier`). The service
 * builds its own question from these fields. ⛔ Must match `AssistRules.CloseIn` (`AssistContract.test.ts`).
 */
export class AppAssistClose {
    public Id: string = "";
    public Label: string = "";
    public Value: string = "";
    public Owner: string = "";
    public Source: string = "";
    public Date: string = "";
    public Time: string = "";
    /** "Write one anyway" - the owner overrode "no message needed". */
    public Force: boolean = false;
}

/** The service's answer. `Draft` is "" when the question did not ask for a message to send. */
export class AppAssistAnswer {
    public SourceKeys: string[] = [];
    public SourceTrust: string = "";
    public Ok: boolean = false;
    public Answer: string = "";
    public Draft: string = "";
    /** The sentence to show as-is when `Ok` is false. */
    public Why: string = "";
    /**
     * The answer had no boolean `ok` - a service without the route (`WAC_ASSIST` unset). The panel
     * shows `NotYetWhy`; the task-close card (`AppTaskNotifier`) hides itself silently.
     */
    public NoRoute: boolean = false;
    /** A closed-task answer (it carried a boolean `notify`). */
    public CloseAware: boolean = false;
    /** Closed-task only: false = the chat shows the contact already knows - no message, no card. */
    public Notify: boolean = true;
    /** Closed-task only, with `Notify` false: why, in a few words, and when (UTC ISO, or ""). */
    public Reason: string = "";
    public ToldIso: string = "";
    /**
     * ⭐ THE PERSISTED HISTORY (2026-09-26): the question, answer and draft are in the contact's
     * `ai-chat.json` in Dropbox. `Ids` are the items added (the page drops its live copy once a reload of
     * the file shows them); `DraftId` is where the outcome is recorded. All false/empty from an older service.
     */
    public Saved: boolean = false;
    /** The history file could not be read, so nothing was added to it. */
    public Unreadable: boolean = false;
    public Ids: string[] = [];
    public DraftId: string = "";
    /**
     * ⭐ A refusal that clears on its own (`{ok:false, why, retry}`, 2026-09-29 - e.g. the service still restoring the
     * archive after a deploy): ask again after this many seconds, 1-600. 0 = a final refusal.
     */
    public RetrySec: number = 0;
}

export class AppAssistApi {
    public static readonly Route: string = "/assist";

    /** ⛔ Must match `AssistRules` - pinned by `AssistContract.test.ts`. */
    public static readonly MaxAsk: number = 2000;
    public static readonly MaxTurns: number = 6;
    public static readonly MaxTurnAsk: number = 2000;
    public static readonly MaxTurnAnswer: number = 4000;
    /** ⛔ Must match `AssistRules.DraftMarker` - the line before the sendable text (`AppTaskNotify.test.ts`). */
    public static readonly DraftMarker: string = "===DRAFT===";

    /** ⛔ Must match `AssistRules.MaxCloseLabel` / `MaxCloseValue` - the page cuts, the service refuses. */
    public static readonly MaxCloseLabel: number = 200;
    public static readonly MaxCloseValue: number = 500;

    /** ⛔ Must match `AssistRules.OutcomeAction` - pinned by `AiChatContract.test.ts`. */
    public static readonly OutcomeAction: string = "outcome";

    /**
     * ⭐ "Keep this" / "Remove" (`AppPins`, `PinRules`). ⛔ Pinned by `PinContract.test.ts`. They live HERE, not in
     * `AppPins`, because `AppPins` reads `AppAssistRules`, which reads this file - a constant there would be a cycle.
     */
    public static readonly PinAction: string = "pin";
    public static readonly UnpinAction: string = "unpin";
    /** The highest block index a pin may name - an answer is 8,000 + 24,000 characters. */
    public static readonly MaxPinBlock: number = 200;

    /**
     * ⭐ "REMOVE FROM HISTORY" (2026-10-01, `AppAssistForget`, `ForgetRules`): one saved question and what came of it
     * taken out of the contact's `ai-chat.json`. ⛔ Must match `ForgetRules.Action` - pinned by `ForgetContract.test.ts`.
     */
    public static readonly ForgetAction: string = "forget";

    /**
     * A pin's block index: a whole number from 0 to `MaxPinBlock`.
     * DOCUMENTED EXCEPTION: pure check of a value in hand. ERROR-SUPPRESSED-JUSTIFIED: cannot throw.
     */
    public static PinBlockOk(Value: unknown): boolean {
        const Block: number = Value as number;
        if (typeof Value !== "number" || Number.isInteger(Block) === false) { return false; }
        return Block >= 0 && Block <= AppAssistApi.MaxPinBlock;
    }

    /**
     * The panel's ready-made questions. Free text is always allowed beside them.
     * ⚠ A chip carries its INDEX, read back against the SAME list at click time (`AppMain.ChipList`), so a
     * new question is APPENDED - never inserted - and nothing stores an index across paints. The chart
     * question (2026-09-27) draws with `WAC_ASSIST_RICH=1`; without it the service answers in words.
     */
    public static readonly Presets: string[] = [
        "What should I say next?",
        "Write a greeting",
        "Summarise what's open",
        "Chart how we've been in touch",
    ];

    /**
     * ⭐ THE CHIP THE SERVICE ANSWERS ITSELF (2026-09-29): "Chart how we've been in touch" is drawn at once from the
     * chat's exact counts (`AssistActivity`) - no model, no daily count. The page sends this ID (`preset`) with the
     * question; ⛔ nothing is ever matched from the question's words. ⛔ Must match `AssistRules.PresetIn`.
     */
    public static readonly ActivityPreset: string = "activity";
    /** Every id `preset` may carry. */
    public static readonly PresetIds: string[] = [AppAssistApi.ActivityPreset];
    /** Where the activity question stands in `Presets` (appended, so it stays put). */
    public static readonly ActivityPresetAt: number = 3;

    /**
     * The preset id of chip `At` of `List` (the list the chips were drawn from), or "" for an ordinary question.
     * ⚠ By POSITION and text together: the task chips follow `Presets` in the same list, so index 3 is the activity
     * question only while `List[3]` still is it.
     */
    public static PresetIdAt = (List: string[], At: number): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (At !== AppAssistApi.ActivityPresetAt || At >= List.length) { return ""; }
            if (List[At] !== AppAssistApi.Presets[AppAssistApi.ActivityPresetAt]) { return ""; }
            return AppAssistApi.ActivityPreset;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistApi.PresetIdAt");
            return "";
        }
    };

    /**
     * The body, or "" when it cannot be asked.
     *
     * ⚠ ONLY THE NEWEST `MaxTurns` TURNS GO, each cut to its limit - a long panel session must keep
     * working rather than start failing at the seventh follow-up. `Page` is the href exactly as the
     * index gave it; the service matches it against its own index and never opens it.
     */
    public static Body = (Phone: string, Key: string, Page: string, Ask: string,
                          Turns: AppAssistTurn[], Preset: string = ""): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (nz(Phone).trim() === "" || nz(Key).trim() === "" || nz(Page).trim() === "") { return ""; }
            const Kept: Record<string, string>[] = [];
            let From: number = 0;
            if (Turns.length > AppAssistApi.MaxTurns) { From = Turns.length - AppAssistApi.MaxTurns; }
            for (const One of Turns.slice(From)) {
                Kept.push({
                    ask: nz(One.Ask).trim().substring(0, AppAssistApi.MaxTurnAsk),
                    answer: nz(One.Answer).trim().substring(0, AppAssistApi.MaxTurnAnswer),
                });
            }
            // `tz`: the browser's own zone - STATISTICS count hours and months on the subscriber's clock.
            const Doc: Record<string, unknown> = { number: nz(Phone).trim(), key: nz(Key).trim(), page: nz(Page).trim(),
                ask: nz(Ask).trim(), turns: Kept, tz: AppTimeZone.Here() };
            // ⭐ `preset` only for a known id - an ordinary question's body is exactly what it was.
            if (AppAssistApi.PresetIds.indexOf(nz(Preset)) >= 0) { Doc.preset = Preset; }
            // DEMO MODE: the question is about the invented archive (`DemoArchive` on the service).
            if (AppDemo.On() === true) { Doc.demo = true; }
            return JSON.stringify(Doc);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistApi.Body");
            return "";
        }
    };

    /**
     * The body for a closed task: an ordinary body (`Ask` is a readable fallback question) plus
     * `close`. "" when it cannot be asked. Label and detail are cut to the service's limits.
     */
    public static CloseBody = (Phone: string, Key: string, Page: string, Ask: string, Close: AppAssistClose): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Plain: string = AppAssistApi.Body(Phone, Key, Page, Ask, []);
            if (Plain.length === 0) { return ""; }
            const Doc: Record<string, unknown> = JSON.parse(Plain) as Record<string, unknown>;
            Doc.close = { id: nz(Close.Id).trim(),
                label: nz(Close.Label).replace(/\s+/g, " ").trim().substring(0, AppAssistApi.MaxCloseLabel),
                value: nz(Close.Value).replace(/\s+/g, " ").trim().substring(0, AppAssistApi.MaxCloseValue),
                owner: nz(Close.Owner), source: nz(Close.Source), date: nz(Close.Date), time: nz(Close.Time),
                force: Close.Force === true };
            return JSON.stringify(Doc);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistApi.CloseBody");
            return "";
        }
    };

    /**
     * The body that records what the owner did with a draft (`sent`, `edited`, `skipped`) on its history
     * item, or "" when it cannot be asked. ⛔ IT RECORDS - THE SERVICE SENDS NOTHING FOR IT.
     */
    public static OutcomeBody = (Phone: string, Key: string, Page: string, Id: string, Outcome: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (nz(Id).trim() === "") { return ""; }
            return AppAssistApi.ActionBody(Phone, Key, Page, AppAssistApi.OutcomeAction, { id: nz(Id).trim(), outcome: nz(Outcome).trim() });
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistApi.OutcomeBody");
            return "";
        }
    };

    /**
     * ⭐ "KEEP THIS": the body that keeps block `Block` of the saved answer `Id` in the contact's Insights, or ""
     * when it cannot be asked. ⛔ IT CARRIES NO MARKUP - the service re-derives the block from the SAVED answer
     * (`AppPins.Derive`). Nothing is sent to anyone.
     */
    public static PinBody = (Phone: string, Key: string, Page: string, Id: string, Block: number): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (nz(Id).trim() === "") { return ""; }
            if (AppAssistApi.PinBlockOk(Block) !== true) { return ""; }
            return AppAssistApi.ActionBody(Phone, Key, Page, AppAssistApi.PinAction, { id: nz(Id).trim(), block: Block });
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistApi.PinBody");
            return "";
        }
    };

    /** "Remove": the body that takes kept item `PinId` out of the contact's Insights, or "" when it cannot be asked. */
    public static UnpinBody = (Phone: string, Key: string, Page: string, PinId: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (nz(PinId).trim() === "") { return ""; }
            return AppAssistApi.ActionBody(Phone, Key, Page, AppAssistApi.UnpinAction, { pin: nz(PinId).trim() });
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistApi.UnpinBody");
            return "";
        }
    };

    /**
     * "Remove from history": the body that takes the saved question holding history item `Id` (any item of it - the
     * question, the answer or its draft) out of the contact's `ai-chat.json`, or "" when it cannot be asked.
     * ⛔ IT REMOVES A RECORD - the service sends nothing to anyone for it, and it carries no text.
     */
    public static ForgetBody = (Phone: string, Key: string, Page: string, Id: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (nz(Id).trim() === "") { return ""; }
            return AppAssistApi.ActionBody(Phone, Key, Page, AppAssistApi.ForgetAction, { id: nz(Id).trim() });
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistApi.ForgetBody");
            return "";
        }
    };

    /**
     * A pin or unpin answer: "" when it was done, otherwise the sentence to show (the service's own, or ours for
     * a lost connection / a service without the route). ⛔ Read whatever the status, like `Parse`.
     */
    public static PinWhy = (Status: number, Text: string): string => {
        if (getCoreUtils().KErrorMode === true) { return AppAssistApi.LostWhy(); }
        try {
            const Answer: AppAssistAnswer = AppAssistApi.Parse(Status, Text, AppAssistApi.LostWhy());
            if (Answer.Ok === true) { return ""; }
            return Answer.Why;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistApi.PinWhy");
            return AppAssistApi.LostWhy();
        }
    };

    /**
     * Read whatever came back.
     *
     * ⛔ THE BODY IS READ WHATEVER THE STATUS (`AppSignUpApi.Answer`'s rule): the service states its
     * refusals IN the body. ⚠ STATUS 0 OR AN UNREADABLE BODY IS `OursWhy` - our end, not the owner's.
     */
    public static Parse = (Status: number, Text: string, OursWhy: string): AppAssistAnswer => {
        if (getCoreUtils().KErrorMode === true) { return AppAssistApi.No(OursWhy); }
        try {
            const Read: AppAnswerDoc = AppAssistApi.ReadAnswerDoc(Status, Text);
            if (Read.Doc === null) { return AppAssistApi.No(OursWhy); }
            const Doc: Record<string, unknown> = Read.Doc;
            // ⚠ NO BOOLEAN `ok` IS NOT A REFUSAL, IT IS A SERVICE WITHOUT THE ROUTE: while `/assist`
            // is switched off (`WAC_ASSIST` unset) an unknown path answers the plain `/status`
            // document, which has no `ok` at all. Read as a refusal it would show `OursWhy` ("at our
            // end, try again") for a feature that is simply not there yet - and read as anything
            // else it would paint an empty answer as a success.
            if (Read.HasOk === false) {
                const Gone: AppAssistAnswer = AppAssistApi.No(AppAssistApi.NotYetWhy());
                Gone.NoRoute = true;
                return Gone;
            }
            if (Doc.ok !== true) {
                let Why: string = nz(Doc.why as string);
                if (typeof Doc.why !== "string" || Why.length === 0) { Why = OursWhy; }
                const Refused: AppAssistAnswer = AppAssistApi.No(Why);
                Refused.RetrySec = AppAssistApi.RetryOf(Doc.retry);
                return Refused;
            }
            const Out: AppAssistAnswer = new AppAssistAnswer();
            Out.Ok = true;
            if (Doc.sourceTrust === AppAssistCitations.Trust) {
                const Keys: string[] | null = AppAssistCitations.KeysIn(Doc.sourceKeys);
                if (Keys !== null) { Out.SourceKeys = Keys; Out.SourceTrust = AppAssistCitations.Trust; }
            }
            if (typeof Doc.answer === "string") { Out.Answer = Doc.answer as string; }
            if (typeof Doc.draft === "string") { Out.Draft = Doc.draft as string; }
            // ⚠ A CLOSED-TASK ANSWER: only an explicit `notify: false` withholds the card; anything
            // else (absent, not a boolean) is a yes - the owner can always Skip.
            if (typeof Doc.notify === "boolean") {
                Out.CloseAware = true;
                Out.Notify = Doc.notify !== false;
                if (typeof Doc.reason === "string") { Out.Reason = Doc.reason as string; }
                if (typeof Doc.toldIso === "string") { Out.ToldIso = Doc.toldIso as string; }
            }
            AppAssistApi.HistoryIn(Doc.history, Out);
            return Out;
        } catch (e) {
            // ERROR-SUPPRESSED-JUSTIFIED: an unreadable body (a proxy's HTML error page) is our end,
            // and `OursWhy` says so on screen; reporting it would blank the page over a network blip.
            return AppAssistApi.No(OursWhy);
        }
    };

    /** The answer's `history` object - each field only when it is the right type (an older service sends none). */
    private static HistoryIn = (Raw: unknown, Out: AppAssistAnswer): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (Raw === null || typeof Raw !== "object" || Array.isArray(Raw) === true) { return; }
            const Bag: Record<string, unknown> = Raw as Record<string, unknown>;
            Out.Saved = Bag.saved === true;
            Out.Unreadable = Bag.unreadable === true;
            if (typeof Bag.draftId === "string") { Out.DraftId = Bag.draftId as string; }
            if (Array.isArray(Bag.ids) === true) {
                for (const One of Bag.ids as unknown[]) { if (typeof One === "string") { Out.Ids.push(One as string); } }
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistApi.HistoryIn");
        }
    };

    /** A `retry` field: a whole number of seconds from 1 to 600, else 0. DOCUMENTED EXCEPTION: pure check. */
    public static RetryOf = (Value: unknown): number => {
        if (typeof Value !== "number" || Number.isInteger(Value) !== true) { return 0; }
        if ((Value as number) < 1 || (Value as number) > 600) { return 0; }
        return Value as number;
    };

    /** DOCUMENTED EXCEPTION: pure constant sentence - no input, cannot throw. */
    public static NotYetWhy = (): string => {
        return "AI help isn't available yet.";
    };

    /**
     * What a lost connection says. ⚠ CALM ON PURPOSE: an answer can take a minute, phones drop
     * connections, and the owner's question is still in the panel to ask again.
     * DOCUMENTED EXCEPTION: pure constant sentence - no input, cannot throw.
     */
    public static LostWhy = (): string => {
        return "Couldn't reach WACommander just now. Your question is still here - try again in a moment.";
    };

    /** A refusal carrying `Why`. DOCUMENTED EXCEPTION: builds a value; cannot throw. */
    private static No = (Why: string): AppAssistAnswer => {
        const Out: AppAssistAnswer = new AppAssistAnswer();
        Out.Why = nz(Why);
        return Out;
    };
    /** DOCUMENTED EXCEPTION: called inside each public action builder reporting boundary. */
    private static ActionBody = (Phone: string, Key: string, Page: string, Action: string, Fields: Record<string, unknown>): string => {
        const Proof: Record<string, unknown> | null = AppAssistApi.ProofFields(Phone, Key, Page, Action);
        if (Proof === null) { return ""; }
        return JSON.stringify({ ...Proof, ...Fields });
    };

    /**
     * ⭐ THE PROVED-PRESS FIELDS `{number, key, page, action}` - ONE place for `/assist`'s actions and `/tasks` +
     * `/money` (`AppTaskApi.ProofOf`) and the warm-up (`AppAssistStream.WarmBody`, whose page may be "" - `PageNeeded`
     * false), or null when a proof field is empty. Each route adds its own fields after these.
     */
    public static ProofFields = (Phone: string, Key: string, Page: string, Action: string,
                                 PageNeeded: boolean = true): Record<string, unknown> | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            if (nz(Phone).trim() === "" || nz(Key).trim() === "") { return null; }
            if (PageNeeded === true && nz(Page).trim() === "") { return null; }
            return { number: nz(Phone).trim(), key: nz(Key).trim(), page: nz(Page).trim(), action: Action };
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistApi.ProofFields");
            return null;
        }
    };

    /**
     * ⭐ THE FIRST STEP OF READING ANY SERVICE ANSWER (`/assist`, `/tasks`, `/export`, `/money`): the body as a JSON
     * object (`Doc` null = a lost connection (status 0), not JSON, or not an object - our end) and whether it carries a
     * boolean `ok` (`HasOk` false = a service without the route, which answers its status document). Each reader
     * decides the sentences.
     */
    public static ReadAnswerDoc = (Status: number, Text: string): AppAnswerDoc => {
        if (getCoreUtils().KErrorMode === true) { return new AppAnswerDoc(); }
        try {
            const Out: AppAnswerDoc = new AppAnswerDoc();
            if (Status === 0) { return Out; }
            const Raw: unknown = JSON.parse(nz(Text));
            if (Raw === null || typeof Raw !== "object" || Array.isArray(Raw) === true) { return Out; }
            Out.Doc = Raw as Record<string, unknown>;
            Out.HasOk = typeof Out.Doc.ok === "boolean";
            return Out;
        } catch (e) {
            // ERROR-SUPPRESSED-JUSTIFIED: a body that is not JSON (a proxy's HTML page) is an expected outside answer;
            // `Doc` null makes every caller say "at our end" on screen - reporting would latch the page over a blip.
            return new AppAnswerDoc();
        }
    };

}
